'use client';

import { useState, useRef, useEffect } from 'react';
import { Button } from '@heroui/button';
import { HeroUIProvider } from '@heroui/react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@heroui/modal';
import { Input } from '@heroui/input';
import { Form } from '@heroui/form';
import { addToast, ToastProvider } from '@heroui/toast';
import { RegMobie } from '@/utils/regRule';
import * as api from './api';

interface Props {
  open?: boolean;
  onClose?: (value?: boolean) => void;
  shopId?: number;
}

function useCountdown(callback: () => void, delay: number | null) {
  const savedCallback = useRef<any>(null);

  useEffect(() => {
    savedCallback.current = callback;
  });

  useEffect(() => {
    const tick = () => {
      savedCallback.current();
    };
    if (delay !== null) {
      const id = setInterval(tick, delay || 0);
      return () => clearInterval(id);
    }
  }, [delay]);
}


export default function Index({ open, onClose, shopId }: Props) {

  const [smsCode, setSmsCode] = useState<string>();
  const [smsToken, setSmsToken] = useState<string>();
  const [isRunning, checkRunning] = useState(false);
  const [second, setSecond] = useState(59);
  const [phone, setPhone] = useState<string>();
  const [name, setName] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);

  // const disableSubmit: boolean = useMemo(() => {
  //   return phone && RegMobie.test(phone) && smsCode && smsCode.length == 4 || false;
  // }, [smsCode, phone]);

  useCountdown(
    () => {
      setSecond(second - 1);
      if (second <= 0) {
        checkRunning(false);
        setSecond(59);
      }
    },
    isRunning ? 1000 : null,
  );

  function onOpenChange() {
    onClose?.();
  }

  function formSubmit() {
    if (!name) {
      addToast({ description: '请输入您的称呼！', color: 'danger', hideIcon: true, timeout: 2000 });
      return;
    }
    if (!phone || !RegMobie.test(phone)) {
      addToast({ description: '请输入正确的手机号！', color: 'danger', hideIcon: true, timeout: 2000 });
      return;
    }
    if (!smsToken) {
      addToast({ description: '请先发送验证码！', color: 'danger', hideIcon: true, timeout: 2000 });
      return;
    }
    if (!smsCode) {
      addToast({ description: '请输入有效的4位验证码！', color: 'danger', hideIcon: true, timeout: 2000 });
      return;
    }
    checkChangeCountAfter();
  };

  async function checkChangeCountAfter() {
    setLoading(true);
    try {
      await api.fetchClueRegisterApi({
        mobile: phone,
        smsCode,
        smsToken,
        name,
        shopId,
      });
      setLoading(false);
      addToast({description: '预约成功！', color: 'success', timeout: 2000})
      onClose?.();
    } catch (e: any) {
      setLoading(false);
      addToast({ description: e.message, color: 'danger', icon: 'none', timeout: 2000 });
    }
  };

  async function sendCode() {
    if (isRunning) {
      addToast({ description: '验证码发送中，请耐心等待！', color: 'warning', icon: 'none', timeout: 2000 });
      return;
    }
    if (!phone || !RegMobie.test(phone)) {
      addToast({ description: '请输入正确的手机号！', color: 'danger', icon: 'none', timeout: 2000 });
      return;
    }
    try {
      const data = await api.verifyCodeApi(phone);
      setSmsToken(data.smsToken);
      checkRunning(true);
    }catch(e: any) {
      addToast({ description: e?.message, icon: 'none', color: 'danger', timeout: 2000 });
    }
  };

  return (
    <HeroUIProvider>
      <ToastProvider maxVisibleToasts={1} placement="top-center" />
      <Modal className="p-0 m-0 rounded-b-none" isOpen={open} placement="bottom" onOpenChange={onOpenChange}>
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex flex-col items-center text-base text-[#333] dark:text-white border-b border-solid border-[#eee] dark:border-[#0a0a0a] gap-1">提交信息</ModalHeader>
              <ModalBody className="p-0">
                <Form className="gap-0">
                  <div
                    className="w-full flex items-center p-[10px] after:content-['']
                      after:block after:absolute after:bottom-0 after:left-0 after:w-full after:h-px after:bg-[rgba(238,238,238,1)] 
                      dark:after:bg-[#0a0a0a] after:origin-bottom after:scale-y-50 relative"
                  >
                    <Input
                      radius="none"
                      errorMessage="请输入您的称呼"
                      label="您的称呼"
                      labelPlacement="outside-left"
                      name="name"
                      classNames={{
                        inputWrapper: 'bg-transparent items-center shadow-none group-data-[focus=true]:bg-transparent',
                        label: 'min-w-[70px] text-[15px] text-[#333] dark:text-white',
                        mainWrapper: 'w-full',
                        base: 'data-[has-helper=true]:items-baseline',
                      }}
                      placeholder="请输入您的称呼"
                      type="text"
                      onValueChange={value => setName(value)}
                    />
                  </div>
                  <div
                    className="w-full flex items-center p-[10px] after:content-['']
                      after:block after:absolute after:bottom-0 after:left-0 after:w-full after:h-px after:bg-[rgba(238,238,238,1)] 
                      dark:after:bg-[#0a0a0a] after:origin-bottom after:scale-y-50 relative"
                  >
                    <Input
                      className="bg-transparent"
                      radius="none"
                      classNames={{
                        inputWrapper: 'bg-transparent shadow-none group-data-[focus=true]:bg-transparent',
                        label: 'min-w-[70px] text-[15px] text-[#333] dark:text-white',
                        mainWrapper: 'w-full',
                        base: 'data-[has-helper=true]:items-baseline',
                      }}
                      errorMessage="请输入您的手机号码"
                      label="手机号码"
                      labelPlacement="outside-left"
                      name="mobile"
                      placeholder="请输入手机号码"
                      onValueChange={value => setPhone(value)}
                      validate={(value: string) => {
                        if (!RegMobie.test(value)) {
                          return '请输入正确的手机号码';
                        }
                        return true;
                      }}
                      type="tel"
                    />
                  </div>
                  <div
                    className="w-full flex items-center p-[10px] after:content-['']
                      after:block after:absolute after:bottom-0 after:left-0 after:w-full after:h-px after:bg-[rgba(238,238,238,1)] 
                      dark:after:bg-[#0a0a0a] after:origin-bottom after:scale-y-50 relative"
                  >
                    <Input
                      radius="none"
                      classNames={{
                        inputWrapper: 'bg-transparent shadow-none group-data-[focus=true]:bg-transparent',
                        label: 'min-w-[70px] text-[15px] text-[#333] dark:text-white',
                        mainWrapper: 'w-full',
                        base: 'data-[has-helper=true]:items-baseline',
                      }}
                      errorMessage="请输入验证码"
                      label="验证码"
                      labelPlacement="outside-left"
                      name="code"
                      maxLength={4}
                      placeholder="请输入验证码"
                      type="text"
                      onValueChange={value => setSmsCode(value)}
                    />
                    <Button onPress={sendCode} variant="light" className="grow w-auto text-[#4189FD] text-sm">
                      {isRunning ? `${second}s后重试` : '获取验证码'}
                    </Button>
                  </div>
                </Form>
              </ModalBody>
              <ModalFooter className="flex flex-col mt-[10px] pl-[15px] pr-[15px]">
                <Button
                  style={{ backgroundImage: 'linear-gradient(90deg, #FFAE01 0%, #FF921C 100%)' }}
                  isLoading={loading}
                  color="warning"
                  onPress={formSubmit}
                  className="bg-gradient-to-tr from-[#FFAE01] to-[#FF921C] text-white shadow-lg grow rounded-[4px] h-[49px] text-[16px]"
                >
                  立即预约
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </HeroUIProvider>
  );
}
